import {MutableRefObject, useMemo, useRef} from 'react';
import {useCanViewAttendanceTrackerAssignments} from '../../../../../hooks/use-can-view-attendance-tracker-assignments';
import {useCanViewAttendanceTrackerRegistry} from '../../../../../hooks/use-can-view-attendance-tracker-registry';
import {Route, Switch, useRouteMatch, Redirect, useLocation} from 'react-router-dom';
import {useAuth} from '../../../../../contexts/auth/context';
import {ScreenPaths} from '../../../../../enums/screen-path';
import {Environment} from '../../../../../enums';
import {useAppContext} from '../../../../../contexts/app/context';
import {Box} from '@mui/material';
import {useOffice} from '../../../../../contexts/office/context';
import {GenericCollapsibleSidePanel} from '../../../../../components/generic-collapsible-side-panel/generic-collapsible-side-panel';
import {TeamSidePanelComponents} from '../../../../../components/generic-collapsible-side-panel/team-side-panel-components';
import AttendanceTrackerRegistryProfessionalCaregiverOfficeScreen from '../professional-caregivers/attendance-tracker/registry/attendance-tracker-registry.professional-caregiver.screen';
import AttendanceTrackerAssignmentsProfessionalCaregiverOfficeScreen from '../professional-caregivers/attendance-tracker/assignments/attendance-tracker-assignments.professional-caregiver.screen';
import PermissionsRequiredRoute from '../../../../../components/permissions-required-route';
import ProfessionalCaregiversProfessionalCaregiverOfficeScreen from '../professional-caregivers/_root/professional-caregivers.professional-caregiver.screen';
import SeniorsProfessionalCaregiverOfficeScreen from '../seniors/_root/seniors.professional-caregiver.screen';
import ProfessionalCaregiverProfessionalCaregiverOfficeScreen from '../professional-caregivers/edit/professional-caregiver.professional-caregiver.screen';
import SettingsProfessionalCaregiverOfficeScreen from '../settings/settings.professional-caregiver.screen';
import SeniorDcmProfessionalCaregiverOfficeScreen from '../seniors/edit/senior-dcm.professional-caregiver.screen';
import ShiftsProfessionalCaregiverProfessionalCaregiverOfficeScreen from '../professional-caregivers/shifts/shifts.professional-caregiver.screen';
import ShiftsAllocationsProfessionalCaregiverOfficeScreen from '../professional-caregivers/shifts-allocations/shifts-allocations.professional-caregiver.screen';
import RolesAndPermissionsProfessionalCaregiverOfficeScreen from '../professional-caregivers/team/permissions/roles-and-permissions.professional-caregiver.screen';
import RolePermissionsProfessionalCaregiverOfficeScreen from '../professional-caregivers/team/permissions/role-permissions.professional-caregiver.screen';
import DashboardProfessionalCaregiverOfficeScreen from '../dashboard/_root/dashboard.professional-caregiver.screen';
import DigitalInclusionProfessionalCaregiverOfficeScreen from '../digital-inclusion/_root/digital-inclusion.professional-caregiver.screen';
import {RolePermissionType} from '../../../../../backend/enums/role-permission-type';
import {CalendarProfessionalCaregiverOfficeScreen} from '../planner/calendar/calendar.professional-caregiver.screen';
import {RemindersProfessionalCaregiverOfficeScreen} from '../planner/reminders/reminders.professional-caregiver.screen';
import {PendingTasksProfessionalCaregiverOfficeScreen} from '../planner/pending-tasks/pending-tasks.professional-caregiver.screen';
import OccurrencesProfessionalCaregiverOfficeScreen from '../occurrences/occurrences.professional-caregiver.screen';
import AccessRequiredRoute from '../../../../../components/access-required-route';
import {ProfessionalsCareServicesProfessionalCaregiverScreen} from '../professional-caregivers/care-services/professionals-service.shifts.professional-caregiver.screen';

export default function ProfessionalCaregiverOffice() {
  // Get context
  const {state: authState, hasReadPermission} = useAuth();

  const officeContext = useOffice();
  const appContext = useAppContext();
  const canAccessDevelopmentFeatures =
    appContext.state.configuration.environment === Environment.Development;

  const canViewAttendanceTrackerAssignments = useCanViewAttendanceTrackerAssignments();
  const canViewAttendanceTrackerRegistry = useCanViewAttendanceTrackerRegistry();

  const showProfessionalCaregiverTeamOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Team_Professionals) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Profile) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Applications) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Cards) ||
      hasReadPermission(RolePermissionType.Team_Professionals_Playlists) ||
      hasReadPermission(RolePermissionType.Team_Shifts_Management) ||
      hasReadPermission(RolePermissionType.Team_Shifts_Attributions) ||
      hasReadPermission(RolePermissionType.Team_Permissions) ||
      canViewAttendanceTrackerAssignments ||
      canViewAttendanceTrackerRegistry,
    [hasReadPermission, canViewAttendanceTrackerAssignments, canViewAttendanceTrackerRegistry]
  );

  // Holds ref
  const selectedEntityIdRef: MutableRefObject<string | undefined> = useRef(
    authState.session?.selectedAffiliation?.entity.id
  );

  // Update ref
  const updateSelectedEntityIdRef = () => {
    // Update it
    selectedEntityIdRef.current = authState.session?.selectedAffiliation?.entity.id;
  };

  // Get location
  let location = useLocation();

  // Routing
  let {path, url} = useRouteMatch();

  const professionalCaregiverRootRoute = `${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Dashboard}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {location.pathname.startsWith(
        `/office/professional-caregiver/entity/${authState.session?.selectedAffiliation?.entity.id}/professional-caregivers`
      ) &&
        showProfessionalCaregiverTeamOptions && (
          <GenericCollapsibleSidePanel>
            <TeamSidePanelComponents
              showExpanded={!officeContext.state.sideNavigationDrawerCollapsed}
              basePath={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`}
            />
          </GenericCollapsibleSidePanel>
        )}
      <Box sx={{width: '100%', minWidth: 0}}>
        <Switch>
          {/* Dashboard */}
          <Route
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_Dashboard}`}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <DashboardProfessionalCaregiverOfficeScreen />;
              } else if (
                selectedEntityIdRef.current !== authState.session?.selectedAffiliation?.entity.id
              ) {
                // Update it
                updateSelectedEntityIdRef();

                // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                return <Redirect to={professionalCaregiverRootRoute} />;
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />
          {/* Seniors */}
          <PermissionsRequiredRoute
            exact
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_Seniors}`}
            requiredPermissions={[
              {
                type: RolePermissionType.Seniors_List,
                canRead: true, // users must be able to at least read the seniors list to access the page
              },
            ]}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <SeniorsProfessionalCaregiverOfficeScreen />;
              } else if (
                selectedEntityIdRef.current !== authState.session?.selectedAffiliation?.entity.id
              ) {
                // Update it
                updateSelectedEntityIdRef();

                // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Seniors}`}
                  />
                );
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />
          <Route
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_Seniors}/:id`}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <SeniorDcmProfessionalCaregiverOfficeScreen />;
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Always redirect to the activity screen
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />
          {/* Professional caregivers */}
          <PermissionsRequiredRoute
            exact
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`}
            requiredPermissions={[
              {
                type: RolePermissionType.Team_Professionals,
                canRead: true, // users must be able to at least read professionals to access the page
              },
            ]}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <ProfessionalCaregiversProfessionalCaregiverOfficeScreen />;
              } else if (
                selectedEntityIdRef.current !== authState.session?.selectedAffiliation?.entity.id
              ) {
                // Update it
                updateSelectedEntityIdRef();

                // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`}
                  />
                );
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />
          {/* Digital inclusion */}
          <Route
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <DigitalInclusionProfessionalCaregiverOfficeScreen />;
              } else if (
                selectedEntityIdRef.current !== authState.session?.selectedAffiliation?.entity.id
              ) {
                // Update it
                updateSelectedEntityIdRef();

                // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`}
                  />
                );
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />
          {/* Shifts */}
          <Route
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}`}
          >
            <Switch>
              <PermissionsRequiredRoute
                exact
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsManagement}`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Team_Shifts_Management,
                    canRead: true, // users must be able to at least read shifts to access the page
                  },
                ]}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Nothing changed
                    return <ShiftsProfessionalCaregiverProfessionalCaregiverOfficeScreen />;
                  } else if (
                    selectedEntityIdRef.current !==
                    authState.session?.selectedAffiliation?.entity.id
                  ) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsManagement}`}
                      />
                    );
                  } else {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                    return <Redirect to={professionalCaregiverRootRoute} />;
                  }
                }}
              />

              <PermissionsRequiredRoute
                exact
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsAllocations}`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Team_Shifts_Attributions,
                    canRead: true, // users must be able to at least read shift allocations to access the page
                  },
                ]}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Nothing changed
                    return <ShiftsAllocationsProfessionalCaregiverOfficeScreen />;
                  } else if (
                    selectedEntityIdRef.current !==
                    authState.session?.selectedAffiliation?.entity.id
                  ) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsAllocations}`}
                      />
                    );
                  } else {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                    return <Redirect to={professionalCaregiverRootRoute} />;
                  }
                }}
              />

              {/** Default route */}
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}`}
              >
                <Redirect
                  to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsManagement}`}
                />
              </Route>
            </Switch>
          </Route>

          {/* Attendance tracker (only while it is enabled in the entity) */}
          {(canViewAttendanceTrackerAssignments || canViewAttendanceTrackerRegistry) && (
            <Route
              path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}`}
            >
              <Switch>
                <PermissionsRequiredRoute
                  exact
                  path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerAssignments}`}
                  requiredPermissions={[
                    {
                      type: RolePermissionType.Team_AttendanceTracker_Assignments,
                      canRead: true, // users must be able to at least read assignments to access the page
                    },
                  ]}
                  render={({
                    match: {
                      params: {entityId},
                    },
                  }) => {
                    if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                      // Update it
                      updateSelectedEntityIdRef();

                      // Nothing changed
                      return <AttendanceTrackerAssignmentsProfessionalCaregiverOfficeScreen />;
                    } else if (
                      selectedEntityIdRef.current !==
                      authState.session?.selectedAffiliation?.entity.id
                    ) {
                      // Update it
                      updateSelectedEntityIdRef();

                      // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                      return (
                        <Redirect
                          to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerAssignments}`}
                        />
                      );
                    } else {
                      // Update it
                      updateSelectedEntityIdRef();

                      // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                      return <Redirect to={professionalCaregiverRootRoute} />;
                    }
                  }}
                />

                <PermissionsRequiredRoute
                  exact
                  path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerRegistry}`}
                  requiredPermissions={[
                    {
                      type: RolePermissionType.Team_AttendanceTracker_Registry,
                      canRead: true, // users must be able to at least read the registry to access the page
                    },
                  ]}
                  render={({
                    match: {
                      params: {entityId},
                    },
                  }) => {
                    if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                      // Update it
                      updateSelectedEntityIdRef();

                      // Nothing changed
                      return <AttendanceTrackerRegistryProfessionalCaregiverOfficeScreen />;
                    } else if (
                      selectedEntityIdRef.current !==
                      authState.session?.selectedAffiliation?.entity.id
                    ) {
                      // Update it
                      updateSelectedEntityIdRef();

                      // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                      return (
                        <Redirect
                          to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerRegistry}`}
                        />
                      );
                    } else {
                      // Update it
                      updateSelectedEntityIdRef();

                      // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                      return <Redirect to={professionalCaregiverRootRoute} />;
                    }
                  }}
                />

                {/** Default route */}
                <Route
                  exact
                  path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}`}
                >
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${
                      canViewAttendanceTrackerAssignments
                        ? ScreenPaths.ProfessionalCaregiver_AttendanceTrackerAssignments
                        : ScreenPaths.ProfessionalCaregiver_AttendanceTrackerRegistry
                    }`}
                  />
                </Route>
              </Switch>
            </Route>
          )}

          <AccessRequiredRoute
            access="care_management"
            exact
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_CareServices}`}
            requiredPermissions={[
              {
                type: RolePermissionType.Team_CareServices,
                canRead: true, // users must be able to at least read care services to access the page
              },
            ]}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <ProfessionalsCareServicesProfessionalCaregiverScreen />;
              } else if (
                selectedEntityIdRef.current !== authState.session?.selectedAffiliation?.entity.id
              ) {
                // Update it
                updateSelectedEntityIdRef();

                // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_CareServices}`}
                  />
                );
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />

          {/* Roles & permissions */}
          <Route
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`}
          >
            <Switch>
              <PermissionsRequiredRoute
                exact
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Team_Permissions,
                    canRead: true, // users must be able to at least read roles to access the page
                  },
                ]}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Nothing changed
                    return <RolesAndPermissionsProfessionalCaregiverOfficeScreen />;
                  } else if (
                    selectedEntityIdRef.current !==
                    authState.session?.selectedAffiliation?.entity.id
                  ) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`}
                      />
                    );
                  } else {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                    return <Redirect to={professionalCaregiverRootRoute} />;
                  }
                }}
              />

              <PermissionsRequiredRoute
                exact
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}/create`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Team_Permissions,
                    canWrite: true, // users must be able to write roles to access the page
                  },
                ]}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Nothing changed
                    return (
                      <RolePermissionsProfessionalCaregiverOfficeScreen
                        mode="create"
                        key="role-permissions-create"
                      />
                    );
                  } else if (
                    selectedEntityIdRef.current !==
                    authState.session?.selectedAffiliation?.entity.id
                  ) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}/create`}
                      />
                    );
                  } else {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                    return <Redirect to={professionalCaregiverRootRoute} />;
                  }
                }}
              />

              <PermissionsRequiredRoute
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}/:roleId`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Team_Permissions,
                    canWrite: true, // users must be able to write roles to access the page
                  },
                ]}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Nothing changed
                    return (
                      <RolePermissionsProfessionalCaregiverOfficeScreen
                        mode="edit"
                        key="role-permissions-edit"
                      />
                    );
                  } else if (
                    selectedEntityIdRef.current !==
                    authState.session?.selectedAffiliation?.entity.id
                  ) {
                    // Update it
                    updateSelectedEntityIdRef();

                    // The selected entity was simply updated by the user. Redirect by updating the entity id in the path.
                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}/:roleId`}
                      />
                    );
                  } else {
                    // Update it
                    updateSelectedEntityIdRef();

                    // Trying to access the screen when the selected entity is not the one in the url. Redirect to dashboard screen.
                    return <Redirect to={professionalCaregiverRootRoute} />;
                  }
                }}
              />

              {/** Default route */}
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`}
              >
                <Redirect
                  to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`}
                />
              </Route>
            </Switch>
          </Route>

          <PermissionsRequiredRoute
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/:id`}
            requiredPermissions={[]}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <ProfessionalCaregiverProfessionalCaregiverOfficeScreen />;
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Always redirect to the activity screen
                return <Redirect to={professionalCaregiverRootRoute} />;
              }
            }}
          />

          {canAccessDevelopmentFeatures && (
            <PermissionsRequiredRoute
              exact
              path={`${path}/:entityId${ScreenPaths.Entity_Planner_Reminders}`}
              requiredPermissions={[
                {type: RolePermissionType.Calendar, canRead: true},
                {type: RolePermissionType.Seniors_PersonalData_Reminders, canRead: true},
              ]}
              render={({
                match: {
                  params: {entityId},
                },
              }) => {
                if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                  updateSelectedEntityIdRef();
                  return <RemindersProfessionalCaregiverOfficeScreen />;
                }

                updateSelectedEntityIdRef();
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.Entity_Planner_Reminders}`}
                  />
                );
              }}
            />
          )}

          {/* Entity Activity Planner */}
          <PermissionsRequiredRoute
            requiredPermissions={[
              {
                type: RolePermissionType.Calendar,
                canRead: true, // users must be able to at least read the calendar to access the page
              },
            ]}
            path={`${path}/:entityId${ScreenPaths.Entity_Planner}`}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                updateSelectedEntityIdRef();

                // Nothing changed
                return <CalendarProfessionalCaregiverOfficeScreen />;
              } else {
                // Update it
                updateSelectedEntityIdRef();

                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.Entity_Planner_Calendar}`}
                  />
                );
              }
            }}
          >
            <Switch>
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.Entity_Planner_Calendar}`}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    updateSelectedEntityIdRef();
                    return <CalendarProfessionalCaregiverOfficeScreen />;
                  } else {
                    updateSelectedEntityIdRef();

                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.Entity_Planner_Calendar}`}
                      />
                    );
                  }
                }}
              />
              <Route
                exact
                path={`${path}/:entityId${ScreenPaths.Entity_Planner_PendingTasks}`}
                render={({
                  match: {
                    params: {entityId},
                  },
                }) => {
                  if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                    updateSelectedEntityIdRef();
                    return <PendingTasksProfessionalCaregiverOfficeScreen />;
                  } else {
                    updateSelectedEntityIdRef();

                    return (
                      <Redirect
                        to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.Entity_Planner_PendingTasks}`}
                      />
                    );
                  }
                }}
              />

              {/*  Default route */}
              <Route path={path}>
                <Redirect to={`${url}/:entityId${ScreenPaths.Entity_Planner_Calendar}`} />
              </Route>
            </Switch>
          </PermissionsRequiredRoute>

          {/* Occurrences */}
          <AccessRequiredRoute
            access="care_management"
            requiredPermissions={[
              {
                type: RolePermissionType.Occurrences,
                canRead: true, // users must be able to at least read the calendar to access the page
              },
            ]}
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_Occurrences}`}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <OccurrencesProfessionalCaregiverOfficeScreen />;
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Updating the entity id in the path.
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Occurrences}`}
                  />
                );
              }
            }}
          />

          {/* Settings */}
          <Route
            path={`${path}/:entityId${ScreenPaths.ProfessionalCaregiver_Settings}`}
            render={({
              match: {
                params: {entityId},
              },
            }) => {
              if (entityId === authState.session?.selectedAffiliation?.entity.id) {
                // Update it
                updateSelectedEntityIdRef();

                // Nothing changed
                return <SettingsProfessionalCaregiverOfficeScreen />;
              } else {
                // Update it
                updateSelectedEntityIdRef();

                // Updating the entity id in the path.
                return (
                  <Redirect
                    to={`${path}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`}
                  />
                );
              }
            }}
          />
          {/** Default route */}
          <Route path={path}>
            <Redirect to={professionalCaregiverRootRoute} />
          </Route>
        </Switch>
      </Box>
    </Box>
  );
}
